import React from 'react';
import TableWindow from '../../../../components/Table/TableWindow';
import availableColumns from './SurveyTemplates.columns';
import useTablePages from '../../../../hooks/useTablePages';
import { useOrderBy } from '../../../../hooks/useOrderBy';
import PageBar from '../../../../components/PageBar/PageBar';
import {
  GET_SURVEY_TEMPLATE_IDS,
  GET_SURVEY_TEMPLATES,
  SurveyTemplateItem,
} from './SurveyTemplates.query';
import { RouteComponentProps, withRouter } from 'react-router';
import Layout from '../../../../components/Layout/Layout';
import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import useStoredFilters from '../../../../hooks/useStoredFilters';
import useThirdPaneCallbacks from '../../../../hooks/useThirdPaneCallbacks';
import { useHistory } from 'react-router-dom';
import useMode from '../../../../hooks/useMode';
import { getStringSearchOr } from '../../../../helpers/search';
import { SearchInput } from '../../../../components/Form';
import { Flex } from 'rebass';
import Button from '../../../../components/Button/Button';
import useSessionInfo from '../../../../hooks/useSessionInfo';

type SurveyTemplateDetailParams = {
  id?: string;
};

type SurveyTemplateDetailProps =
  RouteComponentProps<SurveyTemplateDetailParams>;

export const searchFields = [
  {
    field: 'name',
    label: 'Name',
  },
  {
    field: 'description',
    label: 'Description',
  },
];

const convertStringSearch = (stringSearch?: string) => ({
  or: getStringSearchOr(searchFields, stringSearch),
});

const convertFilters = (storedFilters: any) => {
  return {
    ...convertStringSearch(storedFilters.stringSearch),
  };
};

/**
 * @component for water supplier survey templates page — shows only company-owned templates
 */
function SurveyTemplates({ match }: SurveyTemplateDetailProps): JSX.Element {
  const { session } = useSessionInfo();
  const companyProfileId = session?.companyProfile?.id;

  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<any>(convertFilters, 'waterHazardSurveyTemplates', {
      stringSearch: undefined,
    });

  const { orderBy, onHeaderClick } = useOrderBy(
    'waterHazardSurveyTemplates',
    availableColumns,
    'id'
  );

  // Only show company-owned templates in the management list
  const companyFilter = companyProfileId
    ? { waterSupplierId: companyProfileId, ...queryFilters }
    : queryFilters;

  const {
    loading,
    error,
    itemIds,
    selectedIds,
    setSelectedIds,
    itemLoader,
    refetch,
  } = useTablePages<SurveyTemplateItem>(
    'waterHazardSurveyTemplates',
    GET_SURVEY_TEMPLATE_IDS,
    {
      where: companyFilter,
      limit: null,
      orderBy,
    },
    GET_SURVEY_TEMPLATES,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
      orderBy,
    })
  );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'waterHazardSurveyTemplates');

  const history = useHistory();
  const mode = useMode();

  const { onRowClick } = useThirdPaneCallbacks(
    `/${mode}/survey-templates`,
    itemIds
  );

  return (
    <Layout>
      <Flex
        flexGrow={1}
        flexDirection="column"
        px="mainContent"
        pb="mainContent"
      >
        <PageBar title="My Survey Templates">
          <Button
            ml={3}
            variant="primary"
            icon="plus"
            onClick={() => history.push(`/${mode}/survey-templates/add`)}
          >
            Add Template
          </Button>
        </PageBar>
        <Flex mb={3} alignItems="center">
          <SearchInput
            placeholder="Search templates..."
            value={storedFilters.stringSearch ?? ''}
            onChange={(val: any) =>
              setStoredFilters({ stringSearch: val?.target?.value ?? val })
            }
            width={300}
          />
          {!!storedFilters.stringSearch && (
            <Button
              ml={2}
              variant="outline"
              icon="undo"
              onClick={() => setStoredFilters({ stringSearch: undefined })}
              title="Reset Filters"
            />
          )}
        </Flex>
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            emptyMessage="No company survey templates found. Click 'Add Template' to create one."
            columns={visibleColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            onRowClick={onRowClick}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
          />
        )}
      </Flex>
    </Layout>
  );
}

export default withRouter(SurveyTemplates);
